import { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, Loader2 } from "lucide-react";
import api from "../../lib/api";

/**
 * Jun-2026 (User-Wunsch) — durchsuchbarer SKR51-Kontenauswähler.
 * Backend-Suche über /buchhaltung/skr51/accounts (Kontonr. + Bezeichnung,
 * tokenisiert). Meldet die Auswahl per onChange({konto, bezeichnung}).
 */
export default function Skr51AccountPicker({
    value, onChange, label, accent = "emerald", testid,
    placeholder = "Konto-Nr. oder Bezeichnung suchen…",
}) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState([]);
    const boxRef = useRef(null);
    const timer = useRef(null);

    const search = useCallback(async (term) => {
        setLoading(true);
        try {
            const r = await api.get(`/buchhaltung/skr51/accounts`, { params: { q: term, limit: 25 } });
            setResults(r.data?.accounts || []);
        } catch { setResults([]); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (!open) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => search(q), 250);
        return () => timer.current && clearTimeout(timer.current);
    }, [q, open, search]);

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const accentBorder = accent === "sky" ? "border-sky-300" : "border-emerald-300";
    const accentText = accent === "sky" ? "text-sky-700" : "text-emerald-700";
    const accentBg = accent === "sky" ? "bg-sky-50" : "bg-emerald-50";

    const pick = (a) => { onChange?.(a); setOpen(false); setQ(""); };

    return (
        <div className="relative" ref={boxRef} data-testid={testid}>
            {label && <label className={`text-[10px] font-bold uppercase tracking-wider ${accentText}`}>{label}</label>}
            {value?.konto ? (
                <div className={`flex items-start gap-2 ${accentBg} border ${accentBorder} rounded-sm p-2 mt-1`} data-testid={`${testid}-selected`}>
                    <div className="flex-1 min-w-0">
                        <div className="font-mono-num font-black text-base text-[#111827]">{value.konto}</div>
                        <div className="text-xs text-gray-600 truncate">{value.bezeichnung}</div>
                    </div>
                    <button type="button" onClick={() => onChange?.(null)} className="text-gray-400 hover:text-gray-700" title="Auswahl löschen" data-testid={`${testid}-clear`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <div className="relative mt-1">
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        className="w-full h-10 rounded-sm border border-gray-300 pl-8 pr-3 text-sm"
                        data-testid={`${testid}-input`}
                    />
                </div>
            )}
            {open && !value?.konto && (
                <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-sm shadow-lg" data-testid={`${testid}-results`}>
                    {loading && <div className="px-3 py-3 text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Suche…</div>}
                    {!loading && results.length === 0 && <div className="px-3 py-3 text-sm text-gray-400">Keine Konten gefunden.</div>}
                    {!loading && results.map((a) => (
                        <button
                            key={a.konto}
                            type="button"
                            onClick={() => pick(a)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2"
                            data-testid={`${testid}-result-${a.konto}`}
                        >
                            <span className="font-mono-num font-bold text-[#111827] w-12 shrink-0">{a.konto}</span>
                            <span className="text-sm text-gray-700 truncate">{a.bezeichnung}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
